import {
  formatINR,
  computeBreakdown,
  resolveTiersV2,
  sqft,
  formatDims,
  publicQuotationUrl,
  roomSqftBreakdown,
  roomsSqftBreakdown,
  formatSqftBreakdown,
} from "@/lib/quotationApi";

// Builds the WhatsApp share text for a quotation (pure — no React state).
export function buildQuotationShareText({
  data,
  branding,
  paymentPlans,
  savedId,
  savedStatus,
  savedTiersSnapshot,
  effectiveTierConfigV2,
}) {
  const b = computeBreakdown(data, effectiveTierConfigV2?.modular_multiplier ?? 2.5);
  const tiered = resolveTiersV2(data, savedTiersSnapshot, effectiveTierConfigV2, data.selected_tier_ids);
  const tierList = tiered.list || [];
  // Present tiers highest → lowest for shareable text (and the per-room line).
  const sortedTierList = [...tierList].sort(
    (a, z) => (z.breakdown?.grand_total || 0) - (a.breakdown?.grand_total || 0),
  );
  const lines = [];
  lines.push(`*${branding.firm_name} — Interior Quotation*`);
  if (branding.show_business_info) {
    if (branding.gst_number) lines.push(`GSTIN: ${branding.gst_number}`);
    if (branding.address) lines.push(branding.address);
  }
  lines.push("");
  lines.push(`*Customer:* ${data.customer.name}`);
  if (data.customer.mobile) lines.push(`*Mobile:* ${data.customer.mobile}`);
  if (data.customer.city)
    lines.push(
      `*Location:* ${[data.customer.location, data.customer.city].filter(Boolean).join(", ")}`
    );
  if (data.customer.quotation_given_by)
    lines.push(`*Given by:* ${data.customer.quotation_given_by}`);
  lines.push("");
  lines.push("*Areas & Measurements*");
  data.rooms.forEach((room, ri) => {
    lines.push(`${ri + 1}. ${room.area_type}${room.description ? " — " + room.description : ""}`);
    room.measurements.forEach((m) => {
      const a = sqft(m);
      const mm = effectiveTierConfigV2?.modular_multiplier ?? 2.5;
      const factor = m.is_modular ? mm : 1;
      const cost = a * (parseFloat(m.finish_price) || 0) * factor;
      const modTag = m.is_modular ? ` · Modular ×${mm}` : "";
      lines.push(
        `   • ${formatDims(m)} (${a.toFixed(1)} sqft) · ${m.finish_name || "-"}${modTag} · ${formatINR(cost)}`
      );
    });
    // Per-room sqft total (with modular/non-modular split when applicable)
    lines.push(`   Total: ${formatSqftBreakdown(roomSqftBreakdown(room))}`);
    // per-tier room total (dynamic, highest → lowest)
    const parts = sortedTierList
      .map((ti) => {
        const r = (ti.breakdown.rooms || []).find((x) => x.id === room.id);
        return `${ti.name}: ${formatINR(r?.area_cost || 0)}`;
      })
      .join(" · ");
    if (parts) lines.push(`   ↳ ${parts}`);
  });
  // Grand total sqft across all rooms
  if ((data.rooms || []).length > 0) {
    lines.push(`*Grand total area:* ${formatSqftBreakdown(roomsSqftBreakdown(data.rooms))}`);
  }
  const valid = (data.accessories || []).filter((a) => (a.quantity || 0) > 0);
  if (valid.length) {
    lines.push("");
    lines.push("*Accessories*");
    valid.forEach((a) => {
      lines.push(
        `• ${a.name} × ${a.quantity} · ${formatINR(a.price_per_unit * a.quantity)}`
      );
    });
  }
  if (data.remarks) {
    lines.push("");
    lines.push("*Remarks:* " + data.remarks);
  }
  lines.push("");
  if (b.discount > 0) lines.push(`Discount: -${formatINR(b.discount)}`);
  if (b.taxPct > 0) lines.push(`Tax: ${b.taxPct}%`);
  lines.push("");
  lines.push("*Grand Total Options*");
  sortedTierList.forEach((ti) => {
    const pctNote = ti.finish_discount > 0 ? ` (-${ti.finish_discount}%)` : "";
    lines.push(`• ${ti.name}${pctNote}: *${formatINR(ti.breakdown.grand_total)}*`);
  });
  if (savedId && savedStatus === "approved") {
    lines.push("");
    lines.push(`View online: ${publicQuotationUrl(savedId)}`);
  }
  if (branding.show_payment_info) {
    const payLines = [];
    if (branding.bank_account_name) payLines.push(`Name: ${branding.bank_account_name}`);
    if (branding.bank_name) payLines.push(`Bank: ${branding.bank_name}`);
    if (branding.bank_account_number) payLines.push(`A/c: ${branding.bank_account_number}`);
    if (branding.ifsc_code) payLines.push(`IFSC: ${branding.ifsc_code}`);
    if (branding.upi_id) payLines.push(`UPI: ${branding.upi_id}`);
    if (payLines.length) {
      lines.push("");
      lines.push("*Payment details*");
      payLines.forEach((l) => lines.push(l));
    }
  }
  // Payment plan milestones
  const pp = (paymentPlans || []).find((p) => p.id === data.payment_plan_id);
  if (pp && (pp.milestones || []).length) {
    lines.push("");
    lines.push(`*Payment plan — ${pp.name}*`);
    pp.milestones.forEach((m) => {
      const pct = parseFloat(m.percent) || 0;
      lines.push(`• ${pct}% — ${m.label}${m.note ? ` · ${m.note}` : ""}`);
    });
  }
  const vd = Math.max(1, parseInt(branding.validity_days, 10) || 15);
  lines.push("");
  lines.push(`_Valid for ${vd} ${vd === 1 ? "day" : "days"} only from date of issue._`);
  const note = (branding.footer_note || "").trim();
  if (note) {
    lines.push("");
    lines.push(note);
  }
  return lines.join("\n");
}

// For NEW (unsaved) quotes: refresh each measurement's finish price snapshot
// from the current master finishes. Returns the same state object when nothing
// changed (so setData bails out of a re-render).
export function syncFinishSnapshots(d, finishes) {
  const finishById = new Map(finishes.map((f) => [f.id, f]));
  let changed = false;
  const nextRooms = (d.rooms || []).map((room) => {
    const nextMeas = (room.measurements || []).map((m) => {
      if (!m.finish_id) return m;
      const master = finishById.get(m.finish_id);
      if (!master) return m;
      const masterTp = master.tier_prices || {};
      const masterBase = parseFloat(master.price_per_sqft) || 0;
      const cur = m.finish_tier_prices || {};
      const tpDiffers =
        Object.keys(masterTp).length !== Object.keys(cur).length ||
        Object.keys(masterTp).some(
          (k) => Number(cur[k]) !== Number(masterTp[k]),
        );
      const baseDiffers = Number(m.finish_price) !== masterBase;
      if (!tpDiffers && !baseDiffers) return m;
      changed = true;
      return {
        ...m,
        finish_price: masterBase,
        finish_tier_prices: { ...masterTp },
      };
    });
    return nextMeas === room.measurements
      ? room
      : { ...room, measurements: nextMeas };
  });
  return changed ? { ...d, rooms: nextRooms } : d;
}
